import { Link } from '@tanstack/react-router';
import { Wallet, AlertTriangle, XCircle, RotateCw } from 'lucide-react';
import { formatApiError } from '@mustauto/utils';
import { useBalance } from '@/hooks/useBalance';
import { formatReais } from '@/lib/utils';

export function BalanceWidget() {
  const { data, isLoading, isError, error, refetch } = useBalance();

  // O erro vem ANTES do carregando. Com `isLoading || !data` como gate unico, a
  // busca que falha deixa `isLoading` em false e `data` em undefined — a
  // condicao segue verdadeira e o widget pulsa para sempre. Este e o saldo
  // pre-pago: o numero que decide se as campanhas do parceiro continuam no ar.
  if (isError && !data) {
    return (
      <div className="flex items-center gap-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4" role="alert">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle size={20} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Não foi possível carregar seu saldo</div>
          <div className="text-xs text-muted-foreground">
            {formatApiError(error, 'Ocorreu um erro inesperado.')}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
        >
          <RotateCw size={13} />
          Tentar de novo
        </button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 animate-pulse">
        <div className="h-10 w-10 rounded-full bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-24 rounded bg-muted" />
          <div className="h-3 w-32 rounded bg-muted" />
        </div>
      </div>
    );
  }

  const { currentBalance } = data;
  const isZero = currentBalance <= 0;
  const isLow = currentBalance > 0 && currentBalance < 500;

  return (
    <div
      className={`flex items-center gap-4 rounded-xl border p-4 ${
        isZero
          ? 'border-destructive/30 bg-destructive/5'
          : isLow
            ? 'border-amber-500/30 bg-amber-500/5 dark:border-amber-500/20 dark:bg-amber-500/10'
            : 'border-border bg-card'
      }`}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full ${
          isZero
            ? 'bg-destructive/10 text-destructive'
            : isLow
              ? 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
              : 'bg-primary/10 text-primary'
        }`}
      >
        {isZero ? <XCircle size={20} /> : isLow ? <AlertTriangle size={20} /> : <Wallet size={20} />}
      </div>

      <div className="flex-1">
        <div className="text-lg font-bold">{formatReais(currentBalance)}</div>
        <div className="text-xs text-muted-foreground">
          {isZero
            ? 'Saldo zerado — campanhas pausadas'
            : isLow
              ? 'Saldo baixo — recarregue para manter campanhas ativas'
              : 'Saldo disponível'}
        </div>
      </div>

      <div className="flex gap-2">
        <Link
          to="/balance"
          className="rounded-lg border border-border px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
        >
          Detalhes
        </Link>
        <Link
          to="/balance/top-up"
          className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          Depositar
        </Link>
      </div>
    </div>
  );
}
